import axios, { AxiosResponse } from 'axios';
import { 
  ChatRequest, 
  ChatResponse, 
  SearchRequest, 
  SearchResponse, 
  QuotationRequest, 
  QuotationResponse,
  AnalyticsData,
  ApiResponse
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Response Error:', error);
    
    // Handle common error scenarios
    if (error.response?.status === 401) {
      // Handle unauthorized
      console.warn('Unauthorized access');
    } else if (error.response?.status === 404) {
      console.warn('Resource not found');
    } else if (error.response?.status >= 500) {
      console.error('Server error');
    }
    
    return Promise.reject(error);
  }
);

// Chat API functions
export const chatApi = {
  // Send a chat message
  sendMessage: async (request: ChatRequest): Promise<ChatResponse> => {
    try {
      const response: AxiosResponse<ChatResponse> = await apiClient.post('/api/chat', request);
      return response.data;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw new Error('Failed to send message');
    }
  },

  // Upload and process image
  uploadImage: async (file: File, conversationId?: string): Promise<ChatResponse> => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (conversationId) {
        formData.append('conversation_id', conversationId);
      }

      const response: AxiosResponse<ChatResponse> = await apiClient.post('/api/process-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    }
  },

  // Start new conversation
  startNewConversation: async (): Promise<{ success: boolean; conversation_id: string; message: string }> => {
    try {
      const response = await apiClient.post('/api/new-conversation');
      return response.data;
    } catch (error) {
      console.error('Error starting new conversation:', error);
      throw new Error('Failed to start new conversation');
    }
  },

  // Reset conversation
  resetConversation: async (conversationId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.post(`/api/reset-conversation/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error('Error resetting conversation:', error);
      throw new Error('Failed to reset conversation');
    }
  },
};

// Product API functions
export const productApi = {
  // Get all products with optional filtering
  getProducts: async (category?: string): Promise<{ products: any[]; total: number }> => {
    try {
      const params = category ? { category } : {};
      const response = await apiClient.get('/api/products', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw new Error('Failed to fetch products');
    }
  },

  // Search products
  searchProducts: async (request: SearchRequest): Promise<SearchResponse> => {
    try {
      const response: AxiosResponse<SearchResponse> = await apiClient.get('/api/search', {
        params: request,
      });
      return response.data;
    } catch (error) {
      console.error('Error searching products:', error);
      throw new Error('Failed to search products');
    }
  },

  // Get featured products
  getFeaturedProducts: async (limit = 6): Promise<{ products: any[]; total: number }> => {
    try {
      const response = await apiClient.get('/api/featured-products', { 
        params: { limit } 
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw new Error('Failed to fetch featured products');
    }
  },

  // Refresh product catalog
  refreshCatalog: async (): Promise<{ success: boolean }> => {
    try {
      const response = await apiClient.post('/api/refresh');
      return response.data;
    } catch (error) {
      console.error('Error refreshing catalog:', error);
      throw new Error('Failed to refresh catalog');
    }
  },
};

// Quotation API functions
export const quotationApi = {
  // Generate quote
  generateQuote: async (request: QuotationRequest): Promise<QuotationResponse> => {
    try {
      const response: AxiosResponse<QuotationResponse> = await apiClient.post('/api/quote', request);
      return response.data;
    } catch (error) {
      console.error('Error generating quote:', error);
      throw new Error('Failed to generate quote');
    }
  },

  // Get quotation status
  getQuotationStatus: async (conversationId: string): Promise<any> => {
    try {
      const response = await apiClient.get(`/api/quotation-status/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting quotation status:', error);
      throw new Error('Failed to get quotation status');
    }
  },

  // Export quotation data
  exportQuotationData: async (conversationId: string): Promise<any> => {
    try {
      const response = await apiClient.get(`/api/export-quotation/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error('Error exporting quotation data:', error);
      throw new Error('Failed to export quotation data');
    }
  },

  // Get all quotations (admin)
  getAllQuotations: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/api/all-quotations');
      return response.data;
    } catch (error) {
      console.error('Error getting all quotations:', error);
      throw new Error('Failed to get quotations');
    }
  },

  // Generate comprehensive quotation
  generateQuotation: async (quotationData: any): Promise<any> => {
    try {
      const response = await apiClient.post('/api/generate-quotation', {
        quotation_data: quotationData
      });
      return response.data;
    } catch (error) {
      console.error('Error generating quotation:', error);
      throw new Error('Failed to generate quotation');
    }
  },

  // Send email quotation
  sendEmailQuotation: async (quotationData: any): Promise<any> => {
    try {
      const response = await apiClient.post('/api/send-quotation-email', {
        quotation_data: quotationData
      });
      return response.data;
    } catch (error) {
      console.error('Error sending email quotation:', error);
      throw new Error('Failed to send quotation email');
    }
  },

  // Get all quotation suggestions for staff review
  getQuotationSuggestions: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/api/quotation-suggestions');
      return response.data;
    } catch (error) {
      console.error('Error fetching quotation suggestions:', error);
      throw new Error('Failed to fetch quotation suggestions');
    }
  },

  // Get specific quotation suggestion
  getQuotationSuggestion: async (suggestionId: string): Promise<any> => {
    try {
      const response = await apiClient.get(`/api/quotation-suggestions/${suggestionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching quotation suggestion:', error);
      throw new Error('Failed to fetch quotation suggestion');
    }
  },

  // Accept quotation suggestion
  acceptQuotationSuggestion: async (suggestionId: string, strategyIndex: number = 0): Promise<any> => {
    try {
      const response = await apiClient.post(`/api/quotation-suggestions/${suggestionId}/accept`, {
        strategy_index: strategyIndex
      });
      return response.data;
    } catch (error) {
      console.error('Error accepting quotation suggestion:', error);
      throw new Error('Failed to accept quotation suggestion');
    }
  },

  // Update quotation suggestion with edited strategy
  updateQuotationSuggestion: async (suggestionId: string, strategyIndex: number, strategy: any): Promise<any> => {
    try {
      const response = await apiClient.post(`/api/quotation-suggestions/${suggestionId}/update`, {
        strategy_index: strategyIndex,
        strategy: strategy
      });
      return response.data;
    } catch (error) {
      console.error('Error updating quotation suggestion:', error);
      throw new Error('Failed to update quotation suggestion');
    }
  },
};

// Analytics API functions
export const analyticsApi = {
  // Get analytics data
  getAnalytics: async (): Promise<AnalyticsData> => {
    try {
      const response: AxiosResponse<AnalyticsData> = await apiClient.get('/api/analytics');
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw new Error('Failed to fetch analytics');
    }
  },
};

// Health check
export const healthApi = {
  // Check API health
  checkHealth: async (): Promise<{ status: string; timestamp: string }> => {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      console.error('Error checking health:', error);
      throw new Error('Failed to check API health');
    }
  },
};

// FAQ API functions  
export const faqApi = {
  // Get FAQs
  getFAQs: async (query?: string): Promise<{ count: number; faqs: any[] }> => {
    try {
      const params = query ? { q: query } : {};
      const response = await apiClient.get('/api/faq', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      throw new Error('Failed to fetch FAQs');
    }
  },
};

// Utility functions
export const apiUtils = {
  // Handle API errors
  handleError: (error: any): string => {
    if (error.response?.data?.error) {
      return error.response.data.error;
    } else if (error.message) {
      return error.message;
    } else {
      return 'An unexpected error occurred';
    }
  },

  // Check if API is online
  isOnline: async (): Promise<boolean> => {
    try {
      await healthApi.checkHealth();
      return true;
    } catch {
      return false;
    }
  },

  // Format API response
  formatResponse: <T>(data: T, success = true): ApiResponse<T> => {
    if (success) {
      return { success: true, data };
    } else {
      return { success: false, error: 'Unknown error' };
    }
  },
};

// Export default API client for custom requests
export default apiClient;